// Répartition d'un MONTANT GLOBAL négocié sur les segments d'un parcours.
//
// Sur le terrain, on discute UN prix pour toute la course (« 5 000 pour le
// tour »), pas segment par segment. Mais la feuille, la validation et l'analyse
// du coût par commande travaillent à la ligne : il faut donc répartir.
//
// Logique PURE et isolée : c'est de l'arithmétique sur de l'argent, donc ça se
// teste — et ça se teste sans navigateur ni base.

/**
 * @param {number} total   montant global, en francs entiers
 * @param {number[]} poids un poids par segment (le tarif habituel du trajet).
 *                         Un poids nul ou inconnu vaut 0.
 * @returns {number[]} un montant entier par segment, dont la somme vaut
 *                     EXACTEMENT `total`.
 */
export function repartir(total, poids) {
  const n = Array.isArray(poids) ? poids.length : 0;
  const t = Math.round(Number(total) || 0);
  if (!n || t <= 0) return [];

  const connus = poids.map((x) => (Number(x) > 0 ? Number(x) : 0));

  // ⚠ UN SEGMENT SANS TARIF CONNU NE DOIT PAS RECEVOIR ZÉRO.
  //
  // Constaté en conditions réelles (mode avion, 31/07) : parcours Siège → Lalou
  // → un point sans tarif de référence. Le premier segment ramassait les
  // 5 000 FC, le second était enregistré à 0. Or ce trajet a bien été fait et
  // payé — le déclarer gratuit fausse le coût de la cliente qu'il dessert, et
  // affiche une ligne à 0 FC que la supervision lit comme une erreur de saisie.
  //
  // Un segment inconnu prend donc le poids MOYEN des segments connus : « on ne
  // sait pas ce que coûte ce trajet, on suppose qu'il est ordinaire ». C'est
  // une hypothèse, pas une mesure — mais c'est une hypothèse honnête, alors que
  // zéro est une affirmation fausse.
  const valeursConnues = connus.filter((x) => x > 0);
  const moyenne = valeursConnues.length
    ? valeursConnues.reduce((a, b) => a + b, 0) / valeursConnues.length
    : 0;
  const p = connus.map((x) => (x > 0 ? x : moyenne));
  const somme = p.reduce((a, b) => a + b, 0);

  // AU PRORATA DES TARIFS HABITUELS : un long trajet porte plus qu'un court,
  // ce qui garde le coût par commande crédible. Une répartition uniforme ferait
  // payer autant un aller de 200 m qu'un de 12 km, et fausserait toute analyse
  // de rentabilité par cliente.
  //
  // Aucun tarif connu NULLE PART sur ce parcours (points neufs) : parts égales,
  // faute de mieux — mais on ne fabrique pas de fausse précision.
  const base = somme > 0
    ? p.map((x) => Math.floor((t * x) / somme))
    : p.map(() => Math.floor(t / n));

  // LE RESTE DE LA DIVISION VA AU PREMIER SEGMENT. Sans cela, la somme des
  // lignes serait inférieure au montant payé — de quelques francs, mais la
  // feuille ne collerait plus à la réalité de la caisse, et c'est précisément
  // ce que la validation vérifie.
  const reste = t - base.reduce((a, b) => a + b, 0);
  base[0] += reste;
  return base;
}

/** Les poids d'un parcours : le tarif médian de chaque couple d'arrêts. */
export function poidsDesSegments(arrets, tarifDe) {
  const out = [];
  for (let i = 0; i < Math.max(0, arrets.length - 1); i += 1) {
    const t = tarifDe(arrets[i] && arrets[i].id_point, arrets[i + 1] && arrets[i + 1].id_point);
    out.push(t && Number(t.median) > 0 ? Number(t.median) : 0);
  }
  return out;
}
